import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { moneyApi } from "../services/api";
import SheetHandle from "../components/shared/SheetHandle";
import SavingNotice from "../components/shared/SavingNotice";
import useMinLoad from "../hooks/useMinLoad";
import "./MoneyOverview.css";

const currency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
const monthLabel = (value) =>
  new Date(`${value}T12:00:00`).toLocaleDateString(undefined, {
    month: "short",
  });

function SpendingTrend({ months, anchorMonth, budget }) {
  const rows = (months || [])
    .filter((row) => row.month <= anchorMonth)
    .slice(-6);
  if (!rows.length)
    return (
      <p className="hm-money-trend-empty">No recorded monthly amounts yet.</p>
    );
  const width = 620,
    height = 235,
    left = 48,
    right = 18,
    top = 22,
    bottom = 34;
  const values = rows.map((row) => Number(row.total || 0));
  const ceiling = Math.max(100, Number(budget || 0), ...values) * 1.12;
  const x = (index) =>
    left + (index / Math.max(1, rows.length - 1)) * (width - left - right);
  const y = (value) =>
    top + (1 - Number(value || 0) / ceiling) * (height - top - bottom);
  const line = rows
    .map((row, index) => (index ? "L " : "M ") + x(index) + " " + y(row.total))
    .join(" ");
  const area =
    line +
    " L " +
    x(rows.length - 1) +
    " " +
    (height - bottom) +
    " L " +
    x(0) +
    " " +
    (height - bottom) +
    " Z";
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((part) => ceiling * part);
  return (
    <div className="hm-money-trend-wrap">
      <svg
        className="hm-money-trend"
        viewBox={"0 0 " + width + " " + height}
        role="img"
        aria-label="Six month household spending trend"
      >
        <defs>
          <linearGradient id="hmMoneyArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#2a78d6" stopOpacity=".25" />
            <stop offset="1" stopColor="#2a78d6" stopOpacity=".025" />
          </linearGradient>
        </defs>
        {ticks.map((tick, index) => (
          <g key={index}>
            <line
              x1={left}
              x2={width - right}
              y1={y(tick)}
              y2={y(tick)}
              className="hm-money-trend__grid"
            />
            <text
              x={left - 8}
              y={y(tick) + 4}
              textAnchor="end"
              className="hm-money-trend__axis"
            >
              {currency(tick)}
            </text>
          </g>
        ))}
        {budget != null && (
          <g>
            <line
              x1={left}
              x2={width - right}
              y1={y(budget)}
              y2={y(budget)}
              className="hm-money-trend__budget"
            />
            <text
              x={width - right}
              y={Math.max(12, y(budget) - 7)}
              textAnchor="end"
              className="hm-money-trend__budget-label"
            >
              {currency(budget)} budget
            </text>
          </g>
        )}
        <path d={area} fill="url(#hmMoneyArea)" />
        <path d={line} className="hm-money-trend__line" />
        {rows.map((row, index) => (
          <g className="hm-money-trend__point" key={row.month}>
            <circle
              cx={x(index)}
              cy={y(row.total)}
              r={row.month === anchorMonth ? 5 : 4}
            />
            <title>
              {monthLabel(row.month)}: {currency(row.total)}
            </title>
            <text
              x={x(index)}
              y={height - 9}
              textAnchor="middle"
              className={row.month === anchorMonth ? "is-current" : ""}
            >
              {monthLabel(row.month)}
            </text>
            <text
              x={x(index)}
              y={Math.max(13, y(row.total) - 10)}
              textAnchor="middle"
              className="hm-money-trend__value"
            >
              {currency(row.total)}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function BudgetSheet({ budget, categories, month, busy, onClose, onSave }) {
  const [target, setTarget] = useState(budget?.monthly_target ?? "");
  const [categoryTargets, setCategoryTargets] = useState(
    budget?.category_targets || {},
  );
  const [setAsDefault, setSetAsDefault] = useState(false);
  const monthName = new Date(`${month}T12:00:00`).toLocaleDateString(
    undefined,
    { month: "long", year: "numeric" },
  );
  return (
    <div
      className="hm-money-sheet-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="hm-money-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Set household budget"
      >
        <SheetHandle onClose={onClose} label="Close household budget" />
        <header>
          <div>
            <span>Monthly plan · {monthName}</span>
            <h2>{budget?.is_inherited ? "Customize monthly budget" : "Set monthly budget"}</h2>
          </div>
          <button type="button" onClick={onClose}>
            ×
          </button>
        </header>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onSave({
              month: month.slice(0, 7),
              monthly_target: target || null,
              category_targets: categoryTargets,
              set_as_default: setAsDefault,
            });
          }}
        >
          <label>
            Total monthly budget
            <div>
              <span>$</span>
              <input
                type="number"
                min="0"
                step="1"
                value={target}
                onChange={(event) => setTarget(event.target.value)}
                placeholder="Optional"
              />
            </div>
          </label>
          <p>
            {budget?.is_inherited
              ? "This month currently uses your recurring default. Saving creates a monthly plan without changing other months."
              : "Category targets are optional."}
          </p>
          <div className="hm-money-sheet__categories">
            {categories.map((row) => (
              <label key={row.key}>
                <span>
                  {row.icon} {row.label}
                </span>
                <div>
                  <span>$</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={categoryTargets[row.key] ?? ""}
                    onChange={(event) =>
                      setCategoryTargets((current) => ({
                        ...current,
                        [row.key]: event.target.value,
                      }))
                    }
                    placeholder="—"
                  />
                </div>
              </label>
            ))}
          </div>
          <label className="hm-money-sheet__default">
            <input
              type="checkbox"
              checked={setAsDefault}
              onChange={(event) => setSetAsDefault(event.target.checked)}
            />
            <span>Use these amounts as the recurring default for months without a custom plan</span>
          </label>
          <footer>
            <button type="button" className="is-secondary" onClick={onClose}>
              Cancel
            </button>
            <button disabled={busy}>{busy ? "Saving…" : "Save budget"}</button>
          </footer>
        </form>
      </section>
    </div>
  );
}

export default function MoneyOverview({ standalone = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [savingBudget, setSavingBudget] = useState(false);
  const showSavingNotice = useMinLoad(savingBudget, 700);
  const load = async (month = null) => {
    setBusy(true);
    setError("");
    try {
      setData(await moneyApi.overview(month));
    } catch (err) {
      setError(err.message || "Could not organize household money");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    load();
  }, []);
  const entries = useMemo(
    () =>
      (data?.entries || []).filter(
        (row) =>
          row.month === data?.anchor_month &&
          (!selected || row.category === selected),
      ),
    [data, selected],
  );
  if (!data && !error)
    return (
      <section className="hm-money-loading">
        Organizing spending across HouseMind…
      </section>
    );
  if (error && !data)
    return (
      <section className="hm-money-error">
        {error}
        <button type="button" onClick={() => load()}>
          Try again
        </button>
      </section>
    );
  const summary = data.summary || {};
  // "Expected" means actual + committed everywhere else on this page (the
  // budget meter, the Money nav card) -- keep the hero figure consistent
  // with that instead of quietly folding in "planned" spend too.
  const expected = Number(summary.expected || 0);
  const usedPct = summary.monthly_target
    ? Math.min(
        100,
        Math.round((summary.expected / summary.monthly_target) * 100),
      )
    : null;
  return (
    <section
      className={`hm-money-overview${standalone ? " is-standalone" : ""}`}
    >
      {standalone && (
        <div className="hm-money-masthead">
          <div>
            <span>Household spend</span>
            <strong>{currency(expected)}</strong>
            <small>
              {new Date(`${data.anchor_month}T12:00:00`).toLocaleDateString(
                undefined,
                { month: "long", year: "numeric" },
              )}
            </small>
          </div>
          <button
            className="hm-money-masthead__budget"
            type="button"
            onClick={() => setBudgetOpen(true)}
          >
            {summary.monthly_target ? "Edit budget" : "+ Set budget"}
          </button>
          <nav aria-label="Choose money month">
            {(data.months || []).map((row) => (
                <button
                  type="button"
                  className={row.month === data.anchor_month ? "is-active" : ""}
                  key={row.month}
                  onClick={() => load(row.month.slice(0, 7))}
                >
                  {monthLabel(row.month)}
                </button>
              ))}
          </nav>
        </div>
      )}
      {!standalone && (
        <header className="hm-money-overview__head">
          <div>
            <span>Household money</span>
            <h2>This month, clearly separated</h2>
            <p>
              Confirmed spending, recurring commitments, and plans—without
              counting the same record twice.
            </p>
          </div>
          <button type="button" onClick={() => setBudgetOpen(true)}>
            {summary.monthly_target ? "Edit budget" : "+ Set budget"}
          </button>
        </header>
      )}
      <div className="hm-money-kpis">
        <article className="is-actual">
          <span>Spent</span>
          <strong>{currency(summary.actual)}</strong>
          <small>Confirmed records</small>
        </article>
        <article className="is-committed">
          <span>Committed</span>
          <strong>{currency(summary.committed)}</strong>
          <small>Known recurring costs</small>
        </article>
        <article className="is-planned">
          <span>Planned</span>
          <strong>{currency(summary.planned)}</strong>
          <small>Not spent yet</small>
        </article>
        <article className="is-budget">
          <span>Budget</span>
          <strong>
            {summary.monthly_target == null
              ? "Not set"
              : currency(summary.monthly_target)}
          </strong>
          <small>
            {summary.remaining == null
              ? "Add a monthly target"
              : summary.remaining >= 0
                ? `${currency(summary.remaining)} available`
                : `${currency(Math.abs(summary.remaining))} over expected`}
          </small>
        </article>
      </div>
      {usedPct != null && (
        <div className="hm-money-budget-meter">
          <div>
            <span>Expected against budget</span>
            <strong>{usedPct}%</strong>
          </div>
          <i>
            <b style={{ width: `${usedPct}%` }} />
          </i>
        </div>
      )}
      {data.categories.some((row) => row.target != null) && (
        <div className="hm-money-budget-list">
          <header>
            <span>Budget vs expected</span>
            <strong>By category</strong>
          </header>
          {data.categories
            .filter((row) => row.target != null)
            .map((row) => {
              const used = row.actual + row.committed;
              const pct = row.target
                ? Math.min(100, Math.round((used / row.target) * 100))
                : 0;
              return (
                <article key={row.key}>
                  <div>
                    <span>
                      {row.icon} {row.label}
                    </span>
                    <small>
                      <b>{currency(used)}</b> / {currency(row.target)}
                      {used > row.target ? " · over" : " · available"}
                    </small>
                  </div>
                  <i>
                    <b
                      className={used > row.target ? "is-over" : ""}
                      style={{ width: `${pct}%` }}
                    />
                  </i>
                </article>
              );
            })}
        </div>
      )}
      <div className="hm-money-chart-card">
        <div className="hm-money-chart-card__head">
          <div>
            <span>Six months</span>
            <h3>Household spending trend</h3>
          </div>
          <div className="hm-money-legend">
            <span>
              <i className="actual" />
              Recorded + expected
            </span>
            {summary.monthly_target != null && (
              <span>
                <i className="budget" />
                Budget target
              </span>
            )}
          </div>
        </div>
        <SpendingTrend
          months={data.months}
          anchorMonth={data.anchor_month}
          budget={summary.monthly_target}
        />
      </div>
      <div className="hm-money-category-card">
        <div className="hm-money-category-card__head">
          <div>
            <span>Where it goes</span>
            <h3>All HouseMind categories</h3>
          </div>
          <small>Tap a category for its records</small>
        </div>
        <div className="hm-money-categories">
          {(data.categories || []).map((row) => (
            <button
              type="button"
              key={row.key}
              className={selected === row.key ? "is-selected" : ""}
              onClick={() =>
                setSelected((value) => (value === row.key ? null : row.key))
              }
              style={{ "--money-accent": row.color }}
            >
              <i>{row.icon}</i>
              <span>
                <strong>{row.label}</strong>
                <small>
                  {row.actual
                    ? `${currency(row.actual)} spent`
                    : row.committed
                      ? `${currency(row.committed)} committed`
                      : row.planned
                        ? `${currency(row.planned)} planned`
                        : row.missing_amounts
                          ? "Amount needed"
                          : "No activity"}
                  {row.target != null
                    ? ` · ${currency(row.target)} target`
                    : ""}
                </small>
              </span>
              <b>{currency(row.total)}</b>
            </button>
          ))}
        </div>
        <div className="hm-money-detail">
          <header>
            <strong>
              {selected
                ? data.categories.find((row) => row.key === selected)?.label
                : "This month’s records"}
            </strong>
            <span>{entries.length} with amounts</span>
          </header>
          {entries.length ? (
            entries.slice(0, 8).map((row) => (
              <Link key={row.key} to={row.path}>
                <i className={`is-${row.kind}`} />
                <span>
                  <strong>{row.title}</strong>
                  <small>
                    {row.kind}
                    {row.estimated ? " · estimated" : ""}
                  </small>
                </span>
                <b>{currency(row.amount)}</b>
              </Link>
            ))
          ) : (
            <p>No amount-bearing records in this view yet.</p>
          )}
        </div>
      </div>
      {summary.missing_amounts > 0 && (
        <aside className="hm-money-gaps">
          <span>Amounts still needed</span>
          <strong>
            {summary.missing_amounts} planned or active record
            {summary.missing_amounts === 1 ? " has" : "s have"} no amount
          </strong>
          <p>These records are excluded from totals until an amount is added.</p>
        </aside>
      )}
      <details className="hm-money-method">
        <summary>How HouseMind calculates this</summary>
        <div>
          <p>
            <b>Spent:</b> {data.method.actual}
          </p>
          <p>
            <b>Committed:</b> {data.method.committed}
          </p>
          <p>
            <b>Planned:</b> {data.method.planned}
          </p>
        </div>
      </details>
      {budgetOpen && (
        <BudgetSheet
          budget={data.budget}
          month={data.anchor_month}
          categories={data.categories || []}
          busy={busy}
          onClose={() => setBudgetOpen(false)}
          onSave={async (payload) => {
            setBusy(true);
            setSavingBudget(true);
            setError("");
            try {
              await moneyApi.updateBudget(payload);
              setBudgetOpen(false);
              await load(data.anchor_month.slice(0, 7));
            } catch (err) {
              setError(err.message || "Could not save budget");
              setBusy(false);
            } finally {
              setSavingBudget(false);
            }
          }}
        />
      )}
      {showSavingNotice && <SavingNotice message="Saving household budget..." />}
    </section>
  );
}
